import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  User,
  LogOut,
  ChevronLeft,
  BookOpen,
  Briefcase,
  ScanFace,
  Sparkles,
  Trophy,
  ClipboardList,
  Video,
  Mic,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { User as AuthUser } from '@/types/user'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { hasAssistantAccess } from '@/features/assistant/assistantAccess'
import { hasInstructorFeature, instructorRouteFeature } from '@/lib/instructorFeatures'
import { LiveNowBadge } from '@/features/liveNow/LiveNowBadge'
import { INSTRUCTOR_LIVE_SESSIONS_PATH, useInstructorLiveNow } from '@/features/liveNow/LiveNowNotices'
import { UserAvatar } from './UserAvatar'

const navigation = [
  // R10-14: itens com rota em INSTRUCTOR_ROUTE_FEATURES somem sem a feature nos pacotes.
  { name: 'Dashboard', href: '/instructor/dashboard', icon: LayoutDashboard },
  { name: 'Meus Alunos', href: '/instructor/students', icon: Users },
  { name: 'Central do Aprovado', href: '/instructor/central-do-aprovado', icon: Trophy },
  { name: 'Relatório do ciclo', href: '/instructor/relatorio-do-ciclo', icon: ClipboardList },
  // ZOOM: aulas ao vivo em que o instrutor e panelista.
  { name: 'Reuniões ao vivo', href: '/instructor/aulas-ao-vivo', icon: Video },
  { name: 'Meu Perfil', href: '/instructor/profile', icon: User },
  { name: 'Administrativa', href: '/instructor/administrativa', icon: Briefcase },
  { name: 'Meu espelho', href: '/instructor/espelho', icon: ScanFace },
  // MH9-13: so aparece para o padrinho liberado pelo admin (filtrado abaixo).
  { name: 'Assistente IA', href: '/instructor/assistente', icon: Sparkles, assistant: true },
  // TRANSC-14: download do app desktop que grava e transcreve as 1:1.
  { name: 'Transcritor', href: '/instructor/transcritor', icon: Mic },
]

type NavItem = (typeof navigation)[number]

function isVisible(item: NavItem, user: AuthUser | null): boolean {
  if ('assistant' in item && !hasAssistantAccess(user)) return false
  const feature = instructorRouteFeature(item.href)
  return !feature || hasInstructorFeature(user, feature)
}

export default function InstructorSidebar() {
  const navigate = useNavigate()
  const { logout, user } = useAuthStore()
  const { sidebarCollapsed: isCollapsed, toggleSidebar } = useUiStore()
  // R10-12: selo "Ao vivo" quando o instrutor conduz uma aula agora.
  const conductingNow = useInstructorLiveNow().length > 0

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <>
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-neutral-800 border-r border-neutral-700 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-700">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <span className="font-semibold text-white">Instrutor</span>
                <span className="text-xs text-neutral-400 block">MedHunter</span>
              </div>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors"
          >
            <ChevronLeft className={cn('h-5 w-5 transition-transform', isCollapsed && 'rotate-180')} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navigation.filter((item) => isVisible(item, user)).map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && <span>{item.name}</span>}
              {conductingNow && item.href === INSTRUCTOR_LIVE_SESSIONS_PATH && <LiveNowBadge compact={isCollapsed} />}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-neutral-700">
          {!isCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              <UserAvatar
                avatarUrl={user?.avatar_url}
                initial={user?.full_name?.charAt(0).toUpperCase() || 'I'}
                className="w-8 h-8"
                fallbackClassName="bg-blue-500/20 text-blue-400 font-medium text-sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-neutral-400 truncate">{user?.email}</p>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/app/dashboard')}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-neutral-300 hover:bg-neutral-700 hover:text-white transition-colors mb-2',
              isCollapsed && 'justify-center'
            )}
          >
            <LayoutDashboard className="h-5 w-5" />
            {!isCollapsed && <span>Voltar ao App</span>}
          </button>

          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors',
              isCollapsed && 'justify-center'
            )}
          >
            <LogOut className="h-5 w-5" />
            {!isCollapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  )
}
